import { createFileRoute, redirect } from '@tanstack/react-router'
import { Trans, useLingui, Select } from '@lingui/react/macro'

import shieldIconSrcSet from '@/features/raster-assets/shield_300w.png?w=24;36;72;144&as=srcset&imagetools'
import giftboxIconSrcSet from '@/features/raster-assets/giftbox_300w.png?w=24;36;72;144&as=srcset&imagetools'

import lockIconSrcSet from '@/features/raster-assets/lock_300w.png?w=24;36;72;144&as=srcset&imagetools'

import { Timer } from '@vgw/multibrand-fe-icon'
import { useMemo } from 'react'
import { RedeemEntryPoint } from '@/features/redeem/redeem.lazy'
import { useQuery } from '@tanstack/react-query'
import { getSelfExclusionPlayerOptions } from '@/apis/pam-rsg-api'
import { useSelfExclusionDurationFormatter } from '@/features/self-exclusion/self-exclusion-duration-formatter'
import { CircleLoadingAnimation } from '@vgw/multibrand-fe-circle-loading-animation'
import { LockPagesLayout } from '@/routes/-lock-pages-layout'
import { ContentButton } from '@/features/account-locked/content-button'
import { getAccountCapabilities } from '@/features/ctrls-and-caps/get-account-capabilities'

export const Route = createFileRoute('/_app/account-locked')({
  component: RouteComponent,
  beforeLoad: async () => {
    const { canPlay } = await getAccountCapabilities()

    if (canPlay) {
      throw redirect({ to: '/' })
    }
  },
})

const AccountLockedContent = (props: {
  validTo: string
  type: 'HALF_YEAR' | 'YEAR' | 'INDEFINITE' | 'TEST_20_SEC' | 'TEST_5_MIN'
}) => {
  const formatter = useSelfExclusionDurationFormatter()

  const till = useMemo(() => new Date(props.validTo), [props.validTo])

  const { i18n } = useLingui()

  const periodType =
    till < new Date()
      ? 'ended'
      : props.type === 'INDEFINITE'
        ? 'indefinite'
        : 'defined'

  return (
    <>
      <header data-testid="self-exclusion-header">
        <h1 className="mb-3 text-center text-xl font-bold">
          <Trans>Account Locked</Trans>
        </h1>

        {/*subheading*/}
        <div className="mb-1 text-center font-display font-medium">
          <Trans>Self Exclusion</Trans>
          <br />
          <Trans>
            (
            <Select
              value={periodType}
              _indefinite="indefinite"
              _defined={
                // eslint-disable-next-line lingui/no-trans-inside-trans
                <Trans>
                  {{ time: formatter({ start: new Date(), end: till }) }}{' '}
                  remaining
                </Trans>
              }
              other="Period ended"
            />
            )
          </Trans>
        </div>

        {props.type !== 'INDEFINITE' && (
          <div className="flex items-center justify-center">
            <Timer className="mr-2" />
            {till.toLocaleString(i18n.locale, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </div>
        )}
      </header>

      {/*  links */}
      <div className="mt-4 flex flex-col gap-3 border-y border-border-tertiary-variant/50 py-4">
        <ContentButton to="/account-verification">
          <img
            width={24}
            className="shrink-0"
            height={24}
            srcSet={shieldIconSrcSet}
            alt=""
          />
          <Trans>Account Verification</Trans>
        </ContentButton>
        <ContentButton to="/your-play/access-controls/all">
          <img
            width={24}
            className="shrink-0"
            height={24}
            srcSet={lockIconSrcSet}
            alt=""
          />
          <Trans>Access controls</Trans>
        </ContentButton>
        <ContentButton to="." search={{ redeem: true }}>
          <img
            width={24}
            className="shrink-0"
            height={24}
            srcSet={giftboxIconSrcSet}
            alt=""
          />
          <Trans>Redemption Hub</Trans>
        </ContentButton>
        {/* this one is for the next iterations */}
        {/*<ContentButton to="/">*/}
        {/*  <img*/}
        {/*    width={24}*/}
        {/*    className="shrink-0"*/}
        {/*    height={24}*/}
        {/*    srcSet={discussionIconSrcSet}*/}
        {/*    alt=""*/}
        {/*  />*/}
        {/*  <Trans>Customer Service</Trans>*/}
        {/*</ContentButton>*/}
      </div>
    </>
  )
}

function RouteComponent() {
  const { isPending, error, data } = useQuery({
    ...getSelfExclusionPlayerOptions(),
  })

  if (error) {
    // todo: proper error handling is TBA
    return null
  }

  return (
    <LockPagesLayout>
      {isPending ? (
        <CircleLoadingAnimation />
      ) : (
        <div className="w-full">
          <AccountLockedContent validTo={data.validTo} type={data.type} />
        </div>
      )}
      <RedeemEntryPoint />
    </LockPagesLayout>
  )
}
